import { useCallback, useEffect, useState } from "react";
import { SYSTEMS, SYSTEM_ID } from "../integration";
import { IconClose } from "../icons";

/** Chave no localStorage com os ids de avisos já dispensados pelo usuário. */
const STORAGE_KEY = "sercamp-avisos-dispensados";
/** Intervalo de repolling dos avisos ativos do Portal. */
const POLL_MS = 60_000;

type TipoAviso = "falha" | "manutencao" | "comunicado";

interface Aviso {
  id: number;
  tipo: TipoAviso;
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_em: string;
  expira_em: string | null;
}

function readDismissed(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveDismissed(ids: number[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage indisponível (modo privado etc.) — ignora silenciosamente */
  }
}

/**
 * Banner de avisos do sistema — busca no Portal os avisos ativos (falha,
 * manutenção ou comunicado) publicados pelo Admin Cybersecurity para este
 * sistema e os exibe no topo do app, com opção de dispensar (por usuário,
 * guardado no localStorage). Endpoint público, somente leitura.
 */
export default function AvisoSistema() {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [dismissed, setDismissed] = useState<number[]>(readDismissed);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(
          `${SYSTEMS.portal}/api/security/notificacoes/ativas?sistema=${SYSTEM_ID}`,
        );
        if (!res.ok) return;
        const body = (await res.json()) as Aviso[];
        if (!cancelled && Array.isArray(body)) setAvisos(body);
      } catch {
        /* Portal fora do ar — mantém o último estado conhecido (ou nenhum aviso) */
      }
    }

    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const dismiss = useCallback((id: number) => {
    setDismissed((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      saveDismissed(next);
      return next;
    });
  }, []);

  const visiveis = avisos.filter((a) => !dismissed.includes(a.id));
  if (visiveis.length === 0) return null;

  return (
    <div className="aviso-sistema-stack">
      {visiveis.map((a) => (
        <div key={a.id} className={`aviso-sistema aviso-${a.tipo}`}>
          <span className="aviso-texto">
            <strong>{a.titulo}</strong>
            {a.mensagem ? <>— {a.mensagem}</> : null}
          </span>
          <button
            type="button"
            className="aviso-fechar"
            onClick={() => dismiss(a.id)}
            title="Dispensar aviso"
            aria-label="Dispensar aviso"
          >
            <IconClose size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
